import Dropdown from '@jimmyboys/liita-ui/dist/components/Dropdown'

import styles from './PersonalAreaController.module.sass'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'
import { useLocation } from 'react-router-dom'
import { useMemo } from 'react'
import { useFetchList } from 'store'
import { Options } from 'models'
import useSession from 'hooks/useSession'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import TextSearchInput from 'components/TextSearchInput/TextSearchInput'
import useTermSearch from 'hooks/useTermSearch'
import Datepicker, { DATE_FORMAT } from 'components/Datepicker/Datepicker'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import moment from 'moment'
import { useDeepCompareEffect } from 'react-use'

const NotesFilters = (props) => {
	const {
		className,
		data,
		todayReminders,
		handleDataCollection,
		selectedTab,
	} = props
	const { i18n } = useGlobal()
	const { user: sessionUser } = useSession()
	const location = useLocation()
	const [queryParams, , setQueryParams] = useQueryParams(location)

	const isNotesTab = !selectedTab.length

	const {
		contents: [trustManagers],
	} = useFetchList(
		['/metadata/trust_managers'],
		[Options.fromSimpleObjectJSON]
	)

	const sortOptions = [
		{
			value: 'date,asc',
			text: i18n`Date, asc.`,
		},
		{
			value: 'date,desc',
			text: i18n`Date, desc.`,
		},
	]

	const trustManagersFilter = useMemo(
		() =>
			Array.isArray(trustManagers)
				? [
						{
							value: 'user',
							text: isNotesTab
								? i18n`My Notes`
								: i18n`My Reminders`,
						},
						{
							value: '',
							text: i18n`All`,
						},
						...trustManagers.filter(
							({ value }) => value !== sessionUser.legacyId
						),
				  ]
				: [],
		[i18n, sessionUser.legacyId, trustManagers, isNotesTab]
	)

	const selectedTrustManager = queryParams.claimsManagerTrust
		? trustManagersFilter.find(
				({ value }) =>
					queryParams.claimsManagerTrust.toLocaleLowerCase() ===
					value.toLocaleLowerCase()
		  )
		: null

	const { searchMatches, onSearch, query } = useTermSearch(data, [
		'note',
		'processId',
	])
	const {
		searchMatches: todayRemindersMatches,
		onSearch: todayRemindersOnSearch,
		query: todayRemindersQuery,
	} = useTermSearch(todayReminders, ['note', 'processId'])

	useDeepCompareEffect(() => {
		handleDataCollection(
			{
				matches: searchMatches,
				todayRemindersMatches: todayRemindersMatches,
			},
			{
				query,
				todayRemindersQuery,
			}
		)
	}, [
		searchMatches,
		todayRemindersMatches,
		todayRemindersQuery,
		query,
		isNotesTab,
	])

	const selectedSort = queryParams.sort
		? sortOptions.find(
				({ value }) =>
					queryParams.sort.toLocaleLowerCase() ===
					value.toLocaleLowerCase()
		  )
		: null

	return (
		<HStack justify='space-between'>
			<HStack className={className} gap='small'>
				<VStack>
					<small>
						<strong>
							{isNotesTab ? i18n`Notes` : i18n`Reminders`}
						</strong>
					</small>
					<small className='ellipsis'>{i18n`Order by`}:</small>
				</VStack>
				<div>
					<Dropdown
						className={styles['options-entry']}
						name='sort'
						value={
							selectedSort
								? selectedSort.value
								: sortOptions[0].value
						}
						options={sortOptions}
						onChange={(e) => {
							setQueryParams({
								page: undefined,
								segment: undefined,
								sort: e.target.value,
							})
						}}
					/>
				</div>
				{!isNotesTab && (
					<Datepicker
						className={styles['date-picker']}
						placeholder={i18n.translate(DATE_FORMAT)}
						showFormat={false}
						initialDate={
							queryParams.date
								? moment(queryParams.date, DATE_FORMAT, true)
								: moment().startOf('day')
						}
						type='date'
						text={i18n`date`}
						subText={i18n`From now`}
						onChange={(_, originalValue) => {
							if (originalValue.isValid?.()) {
								setQueryParams({
									date: originalValue.format(DATE_FORMAT),
								})
							} else {
								setQueryParams({
									...queryParams,
									date: undefined,
								})
							}
						}}
					/>
				)}
				<HStack
					className={styles['search-input']}
					align='center'
					justify='flex-start'
					wrap='nowrap'
					gap='xsmall'
				>
					<small>
						<strong>{i18n`Search`}</strong>
					</small>
					<TextSearchInput
						placeholder={i18n`Search by terms`}
						className={styles['search-bar']}
						onKeyUp={(e) => {
							if (!isNotesTab) {
								return onSearch(e) && todayRemindersOnSearch(e)
							} else {
								return onSearch(e)
							}
						}}
						onChange={(e) => {
							if (!isNotesTab) {
								return onSearch(e) && todayRemindersOnSearch(e)
							} else {
								return onSearch(e)
							}
						}}
					/>
				</HStack>
				{trustManagersFilter.length ? (
					<Dropdown
						className={styles['restrict-dropdown']}
						placeholder={
							isNotesTab ? i18n`See notes` : i18n`See reminders`
						}
						name='restrict'
						searchable
						value={
							selectedTrustManager
								? selectedTrustManager.value
								: trustManagersFilter[1].value
						}
						options={trustManagersFilter}
						onChange={(e) => {
							setQueryParams({
								claimsManagerTrust: e.target.value,
							})
						}}
					/>
				) : null}
			</HStack>
		</HStack>
	)
}

export default NotesFilters
